import type { CongressCountdown } from "../../components/ui/CongressCard";
import {
  deriveCongressPhase,
  deriveCongressPresentation,
} from "../../../../api/src/shared/domain/congress-lifecycle";

// The lifecycle itself (phase, the inclusive end, the demotion window) lives in
// `shared/domain/congress-lifecycle.ts`, one definition for admin and webapp.
// This file holds what only the webapp draws from it.

type CongressDates = {
  startDate: string | null;
  endDate: string | null;
  countdownStartsAt: string | null;
};

const DAY_MS = 86_400_000;
const HOUR_MS = 3_600_000;
const MINUTE_MS = 60_000;

/**
 * Whether a space belongs in a HERO feed right now — the Discovery banner and
 * the room's hero block, both of which show a congress worth interrupting
 * someone about.
 *
 * Lives here, and not as a condition repeated in each hero, because the two
 * were already the same rule written twice and this change gives them a third
 * clause. One definition, read by both.
 *
 * Two reasons to be absent, and they are different reasons:
 *
 * 1. THE GRACE WINDOW HAS CLOSED. The congress is archived; it presents as an
 *    ordinary section in the room and has no business in a feed. Before that,
 *    an ENDED congress still belongs — that is the whole point of the window,
 *    and it is what keeps the banner and the room's countdown card agreeing
 *    about whether a congress is still current.
 * 2. THERE IS NOTHING TO COUNT DOWN TO YET. The admin's countdown window has
 *    not opened, or the space carries no dates at all (`evergreen`). Both
 *    predate the grace window and are unchanged by it.
 */
export function isCongressHeroVisible(
  space: CongressDates & {
    featuredDaysAfterEnd: number | null;
    autoDemoteAfterWindow: boolean;
  },
  now: Date
): boolean {
  if (deriveCongressPresentation(space, now) === "section") return false;
  // `deriveCongressCountdown` flattens three unrelated cases into `post`, so
  // ask the lifecycle which one this is: an ended congress inside its window
  // stays, the other two go.
  if (
    deriveCongressCountdown(space, now).phase === "post" &&
    deriveCongressPhase(space, now) !== "post"
  )
    return false;
  return true;
}

/**
 * Derive what the card draws. The countdown has only three display states, and
 * `post` covers every case with nothing left to count down to: the congress is
 * over, it has no dates at all, or the admin's countdown window has not opened
 * yet. That flattening is deliberate for the UI but wrong for analytics, which
 * reads the lifecycle from `deriveCongressPhase` instead.
 *
 * The Discovery hero renders only Pre/Live; the Resources card renders always.
 */
export function deriveCongressCountdown(
  dates: CongressDates,
  now: Date
): CongressCountdown {
  const phase = deriveCongressPhase(dates, now);
  switch (phase) {
    case "evergreen":
    case "post":
      return { phase: "post" };
    case "live":
      return { phase: "live" };
    case "pre": {
      if (dates.countdownStartsAt === null) return { phase: "post" };
      const nowMs = now.getTime();
      if (nowMs < new Date(dates.countdownStartsAt).getTime())
        return { phase: "post" };
      if (dates.startDate === null)
        throw new Error(
          "should not happen: unreachable state — a `pre` phase is only derived when startDate is set"
        );
      const diff = new Date(dates.startDate).getTime() - nowMs;
      return {
        phase: "pre",
        days: Math.floor(diff / DAY_MS),
        hours: Math.floor((diff % DAY_MS) / HOUR_MS),
        minutes: Math.floor((diff % HOUR_MS) / MINUTE_MS),
      };
    }
    default:
      throw new Error(phase satisfies never);
  }
}
